import type { FormData } from "@amzn/aquila-service-type-script-client";
import type { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import {
    Button,
    CardListItem,
    Divider,
    Dropdown,
    DropdownItem,
    Flex,
    Icon,
    RadioButton,
    RadioGroup,
    TabbedNavigation,
    TabbedNavigationItem,
    TextButton,
    Tooltip,
    Heading,
} from "@amzn/storm-ui";
import { copy, sparkle } from "@amzn/storm-ui-icons";
import { useAsync } from "@react-hookz/web";
import { capitalize, isUndefined } from "lodash";
import * as React from "react";
import styled from "styled-components";
import { useStore } from "zustand";

import { getG4SClient } from "../../api/g4sApi/g4sApi";
import type { Email, FeedbackReason } from "../../api/g4sApi/types/operations";
import type { Summary } from "../../api/g4sApi/types/operations/createDeckDecoration";
import {
    DECK_DECORATION_LOADING_TEXT,
    DeckDecorationLanguages,
} from "../../constants/deckDecoration";
import { DeckDecorationType } from "../../constants/deckDecoration";
import { FeedbackPlacement } from "../../constants/feedback";
import {
    G4S_DECORATION_FEEDBACK_REASONS_THUMBS_DOWN,
    G4S_DECORATION_FEEDBACK_REASONS_THUMBS_UP,
} from "../../constants/feedbackReasons";
import { TELEMETRY_ID } from "../../constants/telemetry";
import { useAsyncErrorStatusNotification } from "../../hooks/notifications/useAsyncErrorStatusNotification";
import type { FeedbackOptions } from "../../store/insightDetails";
import { notificationStore, NotificationType } from "../../store/notification";
import {
    AquilaThumbsFeedbackPlacement,
    AquilaThumbsFeedbackReaction,
} from "../../types/aquila";
import type { FeedbackAttributes } from "../../types/feedback";
import { copyToClipboard } from "../../utils/clipboard.utils";
import { submitFeedback } from "../../utils/feedback.utils";
import { logWithTakt } from "../../utils/telemetry.utils";
import { AquilaThumbsFeedback } from "../aquilaThumbsFeedback/AquilaThumbsFeedback";
import SkeletonLoader from "../common/SkeletonLoader";
import {
    DivFlexSpaceBetween,
    DivPadded,
    SpaceBetweenAlignedDiv,
} from "../common/styled.components";
import {
    StyledInsightSheetHeader,
    StyledSheetFooter,
} from "../discovery/insights/insightDetails/styles";

type DeckDecorationContentType = Email | Summary;

const DeckDecorationCardTextBody = styled.code`
    white-space: pre-line;
`;

const DeckDecorationItemHeaderContainer = styled.div`
    display: flex;
    gap: 5px;
    align-items: center;
`;

const StyledPointerIcon = styled(Icon)`
    cursor: pointer;
    &:hover {
        opacity: 0.7;
    }
`;

const StyledPointerFlex = styled(Flex)`
    cursor: pointer;
    &:hover {
        opacity: 0.7;
    }
`;

const DeckDecorationCardContainer = styled.div`
    display: flex;
    flex-flow: column;
    gap: 15px;
`;

const DeckDecorationHeaderContainer = styled(StyledInsightSheetHeader)`
    padding: 0.5rem 1rem;
`;

const DeckDecorationLanguageSelectorItem = styled(CardListItem)`
    &:hover {
        background: ${({ theme }) => theme.color.lightBlue[100]};
    }
`;

const DeckDecorationLanguageSelectorItemContainer = styled.div`
    width: 100%;
    cursor: pointer;
`;

const StyledRadioGroup = styled(RadioGroup)`
    width: 100%;
`;

const CopyButton: React.FunctionComponent<{
    text?: string;
    title: string;
    identifier: string;
    insightType: InsightType;
    responseId: string;
}> = ({ text, title, identifier, insightType, responseId }) => (
    <Tooltip
        transitionDuration={{ enter: 0, exit: 0 }}
        trigger={
            <StyledPointerIcon
                data-testid="deck-decoration-copy-button"
                type={copy}
                size="small"
                onClick={() => {
                    copyToClipboard(text);
                    logWithTakt(TELEMETRY_ID.G4S_COPY_SUMMARY_TO_CLIPBOARD, {
                        type: title,
                        identifier,
                        insightType,
                        responseId,
                    });
                }}
            />
        }
        message={`Copy ${title.toLowerCase()}`}
        nonInteractive
    />
);

const DeckDecorationCardItem: React.FunctionComponent<{
    text?: string;
    title: string;
    identifier: string;
    insightType: InsightType;
    responseId: string;
    loading?: boolean;
    loadHeight?: string;
    loadingText?: string;
}> = ({
    text,
    title,
    identifier,
    insightType,
    responseId,
    loading,
    loadHeight,
    loadingText,
}) => {
    return (
        <CardListItem
            id={""}
            ariaLabel={""}
            title={
                <>
                    <DeckDecorationItemHeaderContainer>
                        <Heading renderAs="h6" color="tertiary">
                            {title}
                        </Heading>
                        <CopyButton
                            text={text}
                            title={title}
                            identifier={identifier}
                            insightType={insightType}
                            responseId={responseId}
                        />
                    </DeckDecorationItemHeaderContainer>
                    <Divider />
                </>
            }
        >
            <DeckDecorationCardTextBody>
                {loading ? (
                    <SkeletonLoader
                        height={loadHeight}
                        loadingText={loadingText}
                    />
                ) : (
                    <>{text}</>
                )}
            </DeckDecorationCardTextBody>
        </CardListItem>
    );
};

export const DeckDecorationViewer: React.FunctionComponent<{
    content?: DeckDecorationContentType;
    type: DeckDecorationType;
    identifier: string;
    insightType: InsightType;
    responseId: string;
    loading?: boolean;
}> = ({ content, type, identifier, insightType, responseId, loading }) => {
    switch (type) {
        case DeckDecorationType.EMAIL:
            return (
                <DeckDecorationCardContainer data-testid="deck-decoration-card-container-email">
                    <DeckDecorationCardItem
                        text={(content as Email)?.subject}
                        title={`${type} Subject`}
                        identifier={identifier}
                        insightType={insightType}
                        responseId={responseId}
                        loading={loading}
                    />
                    <DeckDecorationCardItem
                        text={(content as Email)?.body}
                        title={`${type} Body`}
                        identifier={identifier}
                        insightType={insightType}
                        responseId={responseId}
                        loading={loading}
                        loadHeight="20vh"
                        loadingText={DECK_DECORATION_LOADING_TEXT}
                    />
                </DeckDecorationCardContainer>
            );
        case DeckDecorationType.SUMMARY:
            return (
                <DeckDecorationCardContainer data-testid="deck-decoration-card-container-summary">
                    <DeckDecorationCardItem
                        data-testid={`deck-decoration-card-item-${type}`}
                        text={(content as Summary)?.body}
                        title={`${type} Body`}
                        identifier={identifier}
                        insightType={insightType}
                        responseId={responseId}
                        loading={loading}
                        loadHeight="20vh"
                        loadingText={DECK_DECORATION_LOADING_TEXT}
                    />
                </DeckDecorationCardContainer>
            );
    }
};

export const DeckDecorationHeader: React.FunctionComponent<{
    selectedTab: DeckDecorationType;
    setSelectedTab: (selectedTab: DeckDecorationType) => void;
}> = ({ selectedTab, setSelectedTab }) => {
    return (
        <DeckDecorationHeaderContainer data-testid="deck-decoration-header">
            <TabbedNavigation>
                {Object.values(DeckDecorationType).map((type) => (
                    <TabbedNavigationItem
                        key={type}
                        active={selectedTab === type}
                        label={type}
                        onClick={() => setSelectedTab(type)}
                    />
                ))}
            </TabbedNavigation>
        </DeckDecorationHeaderContainer>
    );
};

export const DeckDecorationHeaderWithLanguage: React.FunctionComponent<{
    selectedTab: DeckDecorationType;
    setSelectedTab: (selectedTab: DeckDecorationType) => void;
    language?: DeckDecorationLanguages;
    setLanguage: (e: DeckDecorationLanguages | undefined) => void;
}> = ({ selectedTab, setSelectedTab, language, setLanguage }) => {
    return (
        <DeckDecorationHeaderContainer data-testid="deck-decoration-header">
            <DivFlexSpaceBetween style={{ alignItems: "center" }}>
                <TabbedNavigation tablist>
                    {Object.values(DeckDecorationType).map((type) => (
                        <TabbedNavigationItem
                            key={type}
                            active={selectedTab === type}
                            label={type}
                            onClick={() => setSelectedTab(type)}
                        />
                    ))}
                </TabbedNavigation>
                <DeckDecorationLanguageDropdown
                    language={language}
                    setLanguage={setLanguage}
                />
            </DivFlexSpaceBetween>
        </DeckDecorationHeaderContainer>
    );
};

const DeckDecorationFeedbackTrigger: React.FunctionComponent<{
    identifier: string;
    insightType: InsightType;
    responseId: string;
    language?: DeckDecorationLanguages;
}> = ({ identifier, insightType, responseId, language }) => {
    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);
    const [feedbackState, feedbackAction] = useAsync(
        async (
            feedbackReasons: FeedbackReason[],
            freeFormFeedback: string,
            rating: number,
        ) => {
            await (
                await getG4SClient()
            ).createFeedback({
                responseId,
                feedbackReasons,
                freeFormFeedback,
                rating,
            });
            upsertNotification(
                "Feedback provided",
                NotificationType.SUCCESS,
                identifier,
            );
        },
    );
    useAsyncErrorStatusNotification(feedbackState);

    const handleFeedbackSubmit = (attr: FeedbackAttributes) => {
        const { reasons: selectedReasons, feedback, rating } = attr;
        const reasons = selectedReasons.map((reason) => ({ reason }));
        void feedbackAction.execute(reasons, feedback, rating);
        submitFeedback(
            { identifier, insightType },
            { language },
            rating,
            FeedbackPlacement.DECK_DECORATION_WITH_METADATA,
        );
        logWithTakt(TELEMETRY_ID.FEEDBACK_SUBMITTED, {
            reasons,
            feedback,
            rating,
            identifier,
            insightType,
            responseId,
            language,
        });
    };

    const handleAquilaThumbsFeedbackSubmit = (
        identifier,
        insightType,
        placement,
        additionalInfo,
    ) => {
        const { feedbackReaction, formData } = additionalInfo;
        const reasons = (formData as FormData[]).find(
            (data) => data.field === "reasons",
        )?.value as string[];
        const selectedReasons = reasons.map((reason) => ({ reason }));
        const feedbackComment = (formData as FormData[]).find(
            (data) => data.field === "comment",
        )?.value as string;
        const feedbackRating =
            feedbackReaction === AquilaThumbsFeedbackReaction.THUMBS_UP ? 1 : 0;
        void feedbackAction.execute(
            selectedReasons,
            feedbackComment,
            feedbackRating,
        );
    };

    const feedbackOptions: FeedbackOptions = {
        thumbsDown: {
            handler: handleFeedbackSubmit,
            reasons: G4S_DECORATION_FEEDBACK_REASONS_THUMBS_DOWN,
        },
        thumbsUp: {
            handler: handleFeedbackSubmit,
            reasons: G4S_DECORATION_FEEDBACK_REASONS_THUMBS_UP,
        },
    };

    return (
        <AquilaThumbsFeedback
            configId="G4sDecorationFeedbackConfig_StormV4"
            identifier={identifier}
            insightType={insightType}
            metadata={{
                language,
                responseId,
            }}
            placement={
                AquilaThumbsFeedbackPlacement.DECK_DECORATION_WITH_METADATA
            }
            onSubmit={handleAquilaThumbsFeedbackSubmit}
        />
    );
};

export const DeckDecorationFooter: React.FunctionComponent<{
    responseId: string;
    identifier: string;
    insightType: InsightType;
    language?: DeckDecorationLanguages;
}> = ({ responseId, identifier, insightType, language }) => {
    return (
        <StyledSheetFooter data-testid="deck-decoration-footer">
            <Flex alignItems="center" justifyContent="space-between">
                <SpaceBetweenAlignedDiv style={{ padding: "0px" }}>
                    <DeckDecorationFeedbackTrigger
                        identifier={identifier}
                        insightType={insightType}
                        responseId={responseId}
                        language={language}
                    />
                </SpaceBetweenAlignedDiv>
                <Heading renderAs="h6" color="tertiary">
                    This summary was created using all the slides in the deck.
                </Heading>
            </Flex>
        </StyledSheetFooter>
    );
};

export const DeckDecorationLanguageDropdown: React.FunctionComponent<{
    language?: DeckDecorationLanguages;
    setLanguage: (e: DeckDecorationLanguages | undefined) => void;
}> = ({ language, setLanguage }) => {
    const OverrideLabel = (dropdownItem, value) => {
        if (value) {
            return dropdownItem;
        }
        return "Select a Language";
    };

    return (
        <Dropdown
            label={""}
            onOverrideLabel={OverrideLabel}
            selectedValue={language}
            onChange={setLanguage}
        >
            {Object.entries(DeckDecorationLanguages).map(([key, value]) => (
                <DropdownItem value={value}>{capitalize(key)}</DropdownItem>
            ))}
        </Dropdown>
    );
};

export const DeckDecorationLanguageHeader: React.FunctionComponent = () => {
    return (
        <DeckDecorationHeaderContainer data-testid="deck-decoration-language-header">
            <div style={{ padding: "10px 0px" }}>
                <Heading renderAs="h3">Please select a language</Heading>
            </div>
        </DeckDecorationHeaderContainer>
    );
};

export const DeckDecorationLanguageList: React.FunctionComponent<{
    setSelectedLanguage: (e: DeckDecorationLanguages | undefined) => void;
    selectedLanguage?: DeckDecorationLanguages;
}> = ({ setSelectedLanguage, selectedLanguage }) => {
    return (
        <DivPadded data-testid="deck-decoration-language-list">
            <StyledRadioGroup
                name="select-language"
                selectedValue={selectedLanguage}
                style={{ width: "100%" }}
            >
                <Flex direction="column" rowGap="xsmall">
                    {Object.entries(DeckDecorationLanguages).map(
                        ([key, value]) => (
                            <DeckDecorationLanguageSelectorItemContainer
                                onClick={() => {
                                    setSelectedLanguage(value);
                                }}
                            >
                                <DeckDecorationLanguageSelectorItem
                                    id={""}
                                    ariaLabel={""}
                                    isSelected={value === selectedLanguage}
                                >
                                    <RadioButton
                                        data-testid={`deck-decoration-language-button-${value}`}
                                        id={value}
                                        value={value}
                                        label={capitalize(key)}
                                        onChange={() => {
                                            setSelectedLanguage(value);
                                        }}
                                    />
                                </DeckDecorationLanguageSelectorItem>
                            </DeckDecorationLanguageSelectorItemContainer>
                        ),
                    )}
                </Flex>
            </StyledRadioGroup>
        </DivPadded>
    );
};

export const DeckDecorationLanguageFooter: React.FunctionComponent<{
    selectedLanguage?: DeckDecorationLanguages;
    handleClose: () => void;
    setLanguage: (e: DeckDecorationLanguages | undefined) => void;
}> = ({ selectedLanguage, handleClose, setLanguage }) => {
    return (
        <StyledSheetFooter data-testid="deck-decoration-language-footer">
            <DivFlexSpaceBetween>
                <TextButton onClick={() => handleClose()}>Cancel</TextButton>
                <Button
                    data-testid="deck-decoration-language-generate-button"
                    disabled={isUndefined(selectedLanguage)}
                    variant="primary"
                    iconStart={sparkle}
                    onClick={() => {
                        setLanguage(selectedLanguage);
                    }}
                >
                    Generate Summary
                </Button>
            </DivFlexSpaceBetween>
        </StyledSheetFooter>
    );
};

export const DeckDecorationLanguageSelector: React.FunctionComponent<{
    handleClose: () => void;
    language?: DeckDecorationLanguages;
    setLanguage: (e: DeckDecorationLanguages | undefined) => void;
}> = ({ handleClose, language, setLanguage }) => {
    const [selectedLanguage, setSelectedLanguage] = React.useState<
        DeckDecorationLanguages | undefined
    >(language);

    return (
        <div data-testid="deck-decoration-language-selector">
            <DeckDecorationLanguageHeader />
            <DeckDecorationLanguageList
                setSelectedLanguage={setSelectedLanguage}
                selectedLanguage={selectedLanguage}
            />
            <DeckDecorationLanguageFooter
                selectedLanguage={selectedLanguage}
                handleClose={handleClose}
                setLanguage={setLanguage}
            />
        </div>
    );
};
